import stringify from 'fast-safe-stringify';

import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { LoggingService } from '@core/services/logging.service';
import { UrlHelperService } from '@core/services/url-helper.service';
import { ProductDetailViewModel, ProductSearchViewModel, StockStatus } from '@models/api-search.models';
import { ProductSchema, Schema } from '@models/structured-data.models';
import { Preset } from '@shared/image/product-image/product-image.component';
import { imageScale } from '@shared/image/helper/image-scale';

@Injectable({
    providedIn: 'root'
})
export class SchemaService {
    constructor(
        @Inject(DOCUMENT)
        private document: Document,
        private loggingService: LoggingService,
        private urlHelperService: UrlHelperService
    ) {}

    // Possible issue with 'object': https://github.com/microsoft/TypeScript/issues/21732
    public setSchema<T extends object = never>(schema: T): void {
        this.setJsonLd(
            stringify({
                '@context': 'https://schema.org',
                ...schema
            })
        );
    }

    public setUmbracoSchema(...schemas: (string | undefined)[]): void {
        schemas.forEach(schema => {
            // If no schema/schema is falsy silently eject
            if (!schema) {
                return;
            }

            if (!this.isValid(schema)) {
                const message = `Tried to set invalid Umbraco schema got: ${schema}`;
                console.warn(`Schema service: ${message}`);
                this.loggingService.logMessage('Schema service', message);
                return;
            }

            // Strip return, newline and tabs from textarea field in Umbraco
            const cleanSchema = schema.replace(/[\r\n\t]/g, '');

            this.setJsonLd(cleanSchema as string);
        });
    }

    public getProductSchema(product: ProductDetailViewModel, currencyCode?: string): ProductSchema {
        return {
            '@context': 'https://schema.org',
            '@type': 'Product',
            'productID': product.variantNo,
            'name': product.productName,
            'brand': {
                '@type': 'Brand',
                'name': product.brand?.text || ''
            },
            'category':
                product.webCategoryL4?.text ||
                product.webCategoryL3?.text ||
                product.webCategoryL2?.text ||
                product.webCategoryL1?.text,
            'description': product.productDescriptionClean,
            'sku': product.variantNo,
            'image': this.buildImage(product.imageUrl, product.productName),
            'offers': {
                '@type': 'Offer',
                'availability': this.availability(product.stockStatus),
                'price': product.unitPriceRaw?.toString(),
                'priceCurrency': currencyCode || '',
                'priceValidUntil': this.priceCampaignValidUntil(product.activeCampaigns, product.campaignOnUse)
            },
            'url': `${this.urlHelperService.getRootDomain()}${product.productUrl}`
        };
    }

    public getListProductSchema(products: ProductSearchViewModel[], currencyCode?: string): Schema {
        const currentUrl = this.urlHelperService.getCurrentUrl().href;

        return {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            'url': currentUrl,
            'numberOfItems': products.length,
            'itemListElement': products.map((product, index) => ({
                '@type': 'ListItem',
                'position': index + 1,
                'item': {
                    '@type': 'Product',
                    'image': this.buildImage(product.imageUrl, product.productName),
                    'url': `${currentUrl}#${product.variantNo}`,
                    'name': product.productName,
                    'offers': {
                        '@type': 'Offer',
                        'price': product.unitPriceRaw,
                        'availability': this.availability(product.stockStatus),
                        'priceCurrency': currencyCode
                    }
                }
            }))
        };
    }

    private availability(stock: StockStatus): 'InStock' | 'SoldOut' | 'LimitedAvailability' {
        if (stock === 'NoStock') {
            return 'SoldOut';
        }
        if (stock === 'Few') {
            return 'LimitedAvailability';
        }
        return 'InStock';
    }

    private setJsonLd(jsonLd: string): void {
        // Avoid duplicate schemas
        if (this.isPresent(jsonLd)) {
            return;
        }

        let scriptElement: HTMLScriptElement | undefined = this.document.createElement('script');
        scriptElement.type = 'application/ld+json';
        scriptElement.innerHTML = jsonLd;
        this.document.head.appendChild(scriptElement);
        scriptElement = undefined;
    }

    private isPresent(jsonLd: string): boolean {
        const jsonLdScripts = this.document.querySelectorAll('script[type="application/ld+json"]');
        return jsonLdScripts?.length > 0 && [...jsonLdScripts].some(script => script.textContent === jsonLd);
    }

    private isValid(str: string) {
        try {
            JSON.parse(str);
        } catch {
            return false;
        }
        return true;
    }

    private priceCampaignValidUntil(
        activeCampaigns: ProductDetailViewModel['activeCampaigns'],
        campaignOnUse?: string
    ): string | undefined {
        return activeCampaigns?.find(campaign => campaign.campaignId === campaignOnUse)?.endDate;
    }

    private buildImage(imageUrl: string, productName: string): string {
        const { width, height } = this.getImageSizes('800|800');
        const image = { src: imageUrl, name: productName, width, height };
        return imageScale(image, image.width, image.height, 'min', 'jpg', 75);
    }

    private getImageSizes(value: Preset): { width: number; height: number } {
        const [width, height] = value.split('|');
        return { width: Number(width), height: Number(height) };
    }
}
